import { motion, useReducedMotion } from "motion/react";
import { useLayoutEffect, useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { EASE_OUT, SPRING_SWAP } from "../../lib/beui/ease";
import { cn } from "../../lib/beui/utils";
import { useTabs, ICON_GAP } from "./TabsContext";

/**
 * A trigger's icon before its label. Shown on every tab, or with
 * `icons="selected"` springing open on the selected tab only.
 */
function TriggerIcon({ icon, active }: { icon: ReactNode; active: boolean }) {
  const { icons } = useTabs();
  const reduce = useReducedMotion();
  if (icons === "always") {
    return (
      <span aria-hidden="true" className="mr-1.5 inline-flex shrink-0 items-center">
        {icon}
      </span>
    );
  }
  return (
    <motion.span
      aria-hidden="true"
      data-tabs-icon=""
      initial={false}
      animate={{
        width: active ? "auto" : 0,
        marginRight: active ? ICON_GAP : 0,
        opacity: active ? 1 : 0,
        scale: active ? 1 : 0.6,
      }}
      transition={reduce ? { duration: 0 } : SPRING_SWAP}
      className="inline-flex shrink-0 items-center justify-center overflow-hidden"
    >
      {icon}
    </motion.span>
  );
}

export function TabsTrigger({
  value,
  icon,
  children,
  className,
  indicatorClassName,
  onClick,
  ...props
}: {
  value: string;
  /** An icon before the label, such as a Lucide icon element. */
  icon?: ReactNode;
  indicatorClassName?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "value">) {
  const {
    value: current,
    setValue,
    layoutId,
    variant,
    icons,
    track,
    placed,
    tabStop,
    registerTab,
    unregisterTab,
    panels,
    tabId,
    panelId,
  } = useTabs();
  const active = current === value;
  const disabled = Boolean(props.disabled);
  useLayoutEffect(() => {
    registerTab(value, disabled);
  }, [registerTab, value, disabled]);
  useLayoutEffect(() => () => unregisterTab(value), [unregisterTab, value]);
  const a11y = {
    id: tabId(value),
    "aria-controls": panels.has(value) ? panelId(value) : undefined,
    tabIndex: !disabled && (tabStop === null || tabStop === value) ? 0 : -1,
  };
  const content = icon ? (
    <>
      <TriggerIcon icon={icon} active={active} />
      {children}
    </>
  ) : (
    children
  );
  // React owns the initial mask only; TabsList synchronizes subsequent masks.
  const [initialClip] = useState(() => active ? "inset(0)" : "inset(0 100% 0 0)");

  if (variant === "underline") {
    return (
      <button
        // Consumer ids and wiring win, e.g. several tabs sharing one panel.
        {...a11y}
        {...props}
        type="button"
        role="tab"
        aria-selected={active}
        onClick={(event) => {
          onClick?.(event);
          if (!event.defaultPrevented) setValue(value);
        }}
        className={cn(
          "relative isolate px-3 pb-2.5 pt-1 -mb-px text-sm font-medium transition-colors min-h-[44px] inline-flex items-center whitespace-nowrap shrink-0",
          active ? "text-navy" : "text-mut hover:text-navy",
          className,
        )}
      >
        {content}
        {active ? (
        <motion.span
          layoutId={layoutId}
          layout
          className={cn(
            "absolute bottom-0 left-0 right-0 h-px bg-navy",
            indicatorClassName,
          )}
        />
        ) : null}
      </button>
    );
  }

  const radius = variant === "pill" ? "rounded-full" : "rounded-md";
  // Tracked pills are placed in the list, so this wrapper must not be their
  // containing block. Server HTML can't measure, so until the first
  // measurement the pill fills the selected tab with CSS, like the others.
  const tracked = icons === "selected" && placed;

  return (
    <div className={cn("shrink-0", !tracked && "relative")}>
      {/* Distinct keys: reused across the swap, the CSS pill's leftover
          `transform: none` would hide the tracked pill's offset until the
          next value change. */}
      {active && tracked ? (
        <motion.span
          key="tracked"
          data-tabs-indicator=""
          style={{ ...track, borderRadius: variant === "pill" ? 9999 : 8 }}
          className={cn("absolute top-0 left-0 bg-navy", radius, indicatorClassName)}
        />
      ) : active ? (
        <motion.span
          key="layout"
          data-tabs-indicator=""
          layoutId={layoutId}
          layout
          style={{ borderRadius: variant === "pill" ? 9999 : 8 }}
          className={cn(
            "absolute inset-0 bg-navy",
            radius,
            indicatorClassName,
          )}
        />
      ) : null}
      <button
        // Consumer ids and wiring win, e.g. several tabs sharing one panel.
        {...a11y}
        {...props}
        type="button"
        role="tab"
        aria-selected={active}
        data-tabs-value={value}
        onClick={(event) => {
          onClick?.(event);
          if (!event.defaultPrevented) setValue(value);
        }}
        className={cn(
          "relative z-10 inline-flex items-center justify-center whitespace-nowrap bg-transparent px-3.5 py-1.5 text-sm font-medium outline-none",
          "text-mut hover:text-navy",
          radius,
          className,
        )}
      >
        {content}
        <span
          data-tabs-label=""
          aria-hidden="true"
          {...{ inert: "" }}
          className="pointer-events-none absolute inset-0 inline-flex items-center justify-center text-white [gap:inherit] [padding:inherit]"
          style={{ clipPath: initialClip }}
        >
          {content}
        </span>
      </button>
    </div>
  );
}

export function TabsContent({ value, children, className }: { value: string; children: ReactNode; className?: string }) {
  const { value: current, registerPanel, unregisterPanel, tabId, panelId } = useTabs();
  const reduce = useReducedMotion();
  const active = current === value;
  useLayoutEffect(() => {
    registerPanel(value);
    return () => unregisterPanel(value);
  }, [registerPanel, unregisterPanel, value]);
  const a11y = {
    id: panelId(value),
    role: "tabpanel",
    "aria-labelledby": tabId(value),
  } as const;
  // Inactive panels stay mounted but hidden, so their content (e.g. source
  // code) is present in the server-rendered HTML for crawlers and assistive
  // tech, instead of being dropped from the DOM.
  if (!active) {
    return (
      <div {...a11y} hidden className={className}>
        {children}
      </div>
    );
  }
  return (
    <motion.div
      {...a11y}
      // Reachable with Tab so a panel without controls can still be read.
      tabIndex={0}
      key={value}
      initial={{ opacity: 0, y: reduce ? 0 : 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduce ? 0 : 0.18, ease: EASE_OUT }}
      className={cn("mt-4 outline-none focus-visible:ring-2 focus-visible:ring-navy/40 rounded-md", className)}
    >
      {children}
    </motion.div>
  );
}
